import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Optional,
  Output,
} from "@angular/core";
import { NgControl } from "@angular/forms";
import { GoogleMapsService } from "src/app/core/google-maps.service";

@Directive({
  selector:
    // Only allowed on mr-textbox of type text (the default type).
    "mr-textbox:not([type])[mrGooglePlace],mr-textbox[type='text'][mrGooglePlace]",
})
export class GooglePlaceDirective implements AfterViewInit, OnDestroy {
  public constructor(
    private readonly element: ElementRef<HTMLElement>,
    private readonly googleMaps: GoogleMapsService,
    @Optional() private readonly ngControl?: NgControl,
  ) {}

  @Output() public readonly addressChange =
    new EventEmitter<GooglePlaceAddress | null>();

  private autocompleteListener?: google.maps.MapsEventListener;

  public async ngAfterViewInit(): Promise<void> {
    const googleMaps = await this.googleMaps.load();

    // Ideally we wouldn't have to query the native element but I'm not sure
    // there's a better way to drill into the host's elements to get this.
    const inputElement = this.element.nativeElement.querySelector("input");
    if (!inputElement) {
      throw new Error(
        "Could not find the input element for the mrGooglePlace directive.",
      );
    }

    const autocomplete = new googleMaps.places.Autocomplete(inputElement, {
      componentRestrictions: {
        country: [
          "us", // United States
          "ca", // Canada
          "mx", // Mexico
        ],
      },
      types: ["(regions)"],
    });

    this.autocompleteListener = autocomplete.addListener(
      "place_changed",
      () => {
        const place = autocomplete.getPlace();
        if (place.formatted_address) {
          this.ngControl?.control?.setValue(place.formatted_address);
        }

        const address = parseAddress(place);
        if (address) {
          this.addressChange.next(address);
        }
      },
    );
  }

  public ngOnDestroy(): void {
    if (this.autocompleteListener) {
      this.autocompleteListener.remove();
    }
  }
}

export interface GooglePlaceAddress {
  readonly city: string | null;
  readonly latitude: number;
  readonly longitude: number;
  readonly state: string | null;
  readonly zip: string | null;
}

function parseAddress({
  address_components: addressComponents,
  geometry,
}: google.maps.places.PlaceResult): GooglePlaceAddress | null {
  if (!addressComponents || !geometry?.location) {
    return null;
  }

  return {
    city: getAddressComponent(addressComponents, "locality"),
    latitude: geometry.location.lat(),
    longitude: geometry.location.lng(),
    state: getAddressComponent(
      addressComponents,
      "administrative_area_level_1",
      "short",
    ),
    zip: getAddressComponent(addressComponents, "postal_code"),
  };
}

function getAddressComponent(
  addressComponents: google.maps.GeocoderAddressComponent[],
  addressComponentType: string,
  format: "long" | "short" = "long",
): string | null {
  return (
    addressComponents.find(({ types }) =>
      types.includes(addressComponentType),
    )?.[`${format}_name` as const] ?? null
  );
}
